/**
 * The one entry point: a named Ember humanoid and a game animation name in, frames out.
 *
 * Pure and deterministic -- no DOM, no pixi -- so the client bakes it into textures, tests read its
 * pixels, and tools write it to PNG, all from the same code.
 */
import {
    EMBER_ARMOR_NAMES,
    EMBER_BAULDRON_NAMES,
    EMBER_HELD_NAMES,
    POLEARMS,
    TWO_HANDED,
    EMBER_PANTS_NAMES,
    EMBER_SHIELD_NAMES,
    EMBER_SHIRT_NAMES,
    EMBER_SHOES_NAMES,
    EmberLook,
    buildPrimitives,
    fistCenter,
    wornArmor,
    wornBauldron,
    wornPants,
    wornShirt,
    wornShoes,
} from "./body"
import { emberDesign, emberModelsRevision } from "./model"
import { isIdleVariation, THREATEN_ACTIONS, CARRY_CLIPS, CLIP_FOR_ACTION, EMBER_CLIPS, EmberClip, LPC_ACTION_DURATION_MS, ShotAim, atRest, bearingShield, carrying, clipTime, layerClip, poleCarry, shambling } from "./clips"
import { EMBER_PERFORMANCES, isEmberPerformance } from "./animations"
import { isEmberSpeciesBody, planSpeciesAnimation } from "./species"
import { FROM_REST, SETTLE, SETTLE_SPEED, STANCE_ATTACKS, STRIDE_SETTLES, settleFromPose, settleFromStride, standingAttack } from "./settle"
import { faceStamps } from "./face"
import { bodyStamps } from "./marks"
import { EMBER_BODY_COLORS, EMBER_HUMANOIDS, EmberHumanoid } from "./humanoids"
import { DEFAULT_STYLE, EMBER_STYLES, EmberStyle } from "./styles"
import { FACINGS, FACING_YAW, Facing, Frame, HEAD_COUNTER_TURN, SIDE_OFFSET, STRIDE_ACROSS_SCREEN, facePrimitives, facingScreenAngle, frameFoot, FrameFoot, projectPoint, rasterize } from "./raster"
import { ArmPose, DEFAULT_PROPORTIONS, Proportions, SIDES, Side, solvePose } from "./rig"
import { Vec3, add, apply, dot, rotY, scale } from "./vec"

export interface EmberAnimation {
    /** The game's name for it, e.g. `walk-left`. */
    name: string
    clip: string
    facing: Facing
    frames: Frame[]
    frameMs: number
    loop: boolean
    /** Ground one cycle covers, for locomotion; the client times the cycle from ground speed. */
    strideCycle?: number
    /** For an attack on the move: where the gait's stride has got to when it ends (0..1). */
    gaitPhaseAfter?: number
    /** The arms hold a load in front (EmberEquipment.carry): the client draws it between the hands. */
    carrying?: boolean
}

export const proportionsFor = (overrides?: Partial<Proportions>): Proportions => ({
    ...DEFAULT_PROPORTIONS,
    ...(overrides || {}),
})

/** The skeleton a clip puts a body in at `t`, and the camera it is drawn with, for a facing. */
const posed = (proportions: Proportions, clip: EmberClip, t: number, facing: Facing, style: EmberStyle) => {
    let raster = style.raster
    const pose = clip.sample(t, proportions, facing)
    // In a side facing the head turns back toward profile from the body's three-quarter stance
    // (see SIDE_TURN): toward the near side's opposite, whichever way the figure faces.
    if (facing === "left") pose.headYaw -= HEAD_COUNTER_TURN
    if (facing === "right") pose.headYaw += HEAD_COUNTER_TURN
    if (facing === "left" || facing === "right") {
        const toward = facing === "left" ? -1 : 1
        raster = { ...raster, centerX: raster.centerX + toward * SIDE_OFFSET.x, groundY: raster.groundY - SIDE_OFFSET.y }
    }
    return { skeleton: solvePose(pose, proportions), raster, pose }
}

/** Where on a frame (pixels from its top-left) a body's head and fists are: for effects that follow them. */
export interface EmberJoints {
    head: { x: number; y: number }
    /** Left fist, right fist. */
    hands: [{ x: number; y: number }, { x: number; y: number }]
    /** Each foot: the ground under it, how high it is off it, whether the body hides it. */
    feet?: FrameFoot[]
}

export const clipJoints = (proportions: Proportions, clip: EmberClip, t: number, facing: Facing, style: EmberStyle): EmberJoints => {
    const { skeleton, raster, pose } = posed(proportions, clip, t, facing, style)
    const turn = rotY(FACING_YAW[facing])
    const at = (v: Vec3) => projectPoint(apply(turn, v), raster)
    const middle = scale(add(skeleton.L.hip, skeleton.R.hip), 0.5)
    // The sole is a footRadius below the heel and toe points, whichever is lower.
    const feet = SIDES.map(side => {
        const { heel, toe, hip } = skeleton[side]
        const sole: Vec3 = [(heel[0] + toe[0]) / 2, Math.min(heel[1], toe[1]) - proportions.footRadius, (heel[2] + toe[2]) / 2]
        const leg = pose.legs[side]
        return frameFoot(sole, hip, middle, v => apply(turn, v), raster, leg.mode === "ik" ? leg.planted : undefined)
    })
    return { head: at(skeleton.head), hands: [at(fistCenter(skeleton, proportions, "L")), at(fistCenter(skeleton, proportions, "R"))], feet }
}

export const renderClipFrame = (
    look: EmberLook,
    proportions: Proportions,
    clip: EmberClip,
    t: number,
    facing: Facing,
    style: EmberStyle = EMBER_STYLES[DEFAULT_STYLE],
): Frame => {
    const { skeleton, raster } = posed(proportions, clip, t, facing, style)
    const skin = style.skins[look.skin] ?? style.skins.light
    return rasterize(
        facePrimitives(buildPrimitives(skeleton, proportions, look, facing, style.skins), facing),
        raster,
        [
            // A skull's sockets are its own (body.ts bonesOf): no muscle marks, no eyes.
            ...(style.marks && !look.bones ? bodyStamps(skeleton, proportions, skin, facing, raster, style.marks) : []),
            ...(look.bones ? [] : faceStamps(skeleton, proportions, skin, facing, look.eyes ?? style.eyes, raster)),
        ],
    )
}

/** `walk-left` -> walk, left. A bare name (no facing) faces the camera. */
/**
 * An attack thrown while walking or running is named `<attack>.<gait>.<phase>[.<speed>]-<facing>`,
 * e.g. `slash.walk.3.60-left`: the attack's upper body over the gait's legs, which start at stride
 * phase `phase / MOVING_ATTACK_PHASES` -- where the walk had got to -- so the legs carry on without
 * a jump, and cover the ground the body crosses at `speed` px/s (rounded to MOVING_ATTACK_SPEED_STEP)
 * while the attack plays, so they keep the walk's own pace instead of churning.
 */
export const MOVING_ATTACK_PHASES = 8
export const MOVING_ATTACK_GAITS = ["walk", "run"]

/** Ground speed (px/s) the legs are timed for when a name gives none. */
export const MOVING_ATTACK_SPEED: Record<string, number> = { walk: 100, run: 140 }
/** Speeds are rounded to this, so a body's pace picks one of a few variants rather than a new one each time. */
export const MOVING_ATTACK_SPEED_STEP = 10

export const movingAttackName = (attack: string, gait: string, phase: number, facing: Facing, speed?: number): string => {
    const at = ((Math.round(phase * MOVING_ATTACK_PHASES) % MOVING_ATTACK_PHASES) + MOVING_ATTACK_PHASES) % MOVING_ATTACK_PHASES
    const pace = speed === undefined ? "" : `.${Math.max(MOVING_ATTACK_SPEED_STEP, Math.round(speed / MOVING_ATTACK_SPEED_STEP) * MOVING_ATTACK_SPEED_STEP)}`
    return `${attack}.${gait}.${at}${pace}-${facing}`
}

/**
 * A shot aimed along a screen heading carries it in its name, before the facing: `.aim<k>` is a
 * heading of k SHOT_AIM_STEPs clockwise from screen right. `shoot.aim5-frontright` flies at 45
 * degrees, `shoot.walk.3.120.aim6-frontright` at 54 on the move. Headings come in steps so a body
 * bakes a few dozen shots rather than one per shot, and each stays within SHOT_AIM_REACH of its facing.
 */
export const SHOT_AIM_STEPS = 40
const SHOT_AIM_STEP = (2 * Math.PI) / SHOT_AIM_STEPS
const SHOT_AIM_REACH = Math.PI / 4
const AIM_SEGMENT = /\.aim(\d+)$/
const wrapAngle = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a))

/** `animation` (a shot) aimed along screen heading `heading` (radians), to the step it falls in. */
export const aimedShotName = (animation: string, heading: number): string => {
    const [name, direction] = animation.split("-")
    const bare = name.replace(AIM_SEGMENT, "")
    const parsed = parseAnimationName(`${bare}-${direction}`)
    if (!parsed || !Number.isFinite(heading)) return animation
    const centre = facingScreenAngle(parsed.facing)
    const off = Math.max(-SHOT_AIM_REACH, Math.min(SHOT_AIM_REACH, wrapAngle(heading - centre)))
    const step = ((Math.round((centre + off) / SHOT_AIM_STEP) % SHOT_AIM_STEPS) + SHOT_AIM_STEPS) % SHOT_AIM_STEPS
    return `${bare}.aim${step}-${direction}`
}

/**
 * The aim (clips.ts ShotAim) that draws a shot along screen heading `heading` for a body facing
 * `facing`, under a camera pitched `cameraPitch` down.
 *
 * That camera looks at a body nearly level, while the game shows the ground nearly from above: a bow
 * held level along a diagonal facing is drawn nearly flat on screen (a frontright archer's arrow lay
 * at 8 degrees for every shot it loosed between 22.5 and 67.5). Every direction this camera draws
 * along the heading lies in one plane, through the heading's line across the screen and the line of
 * sight; the aim is the one in it nearest the way the body faces, so the body turns and tilts no
 * further than it must.
 */
export const shotAim = (facing: Facing, heading: number, cameraPitch: number): ShotAim => {
    const facingYaw = FACING_YAW[facing]
    const forward: Vec3 = [Math.sin(facingYaw), 0, Math.cos(facingYaw)]
    // Camera space: x to screen right, y up, z toward the viewer.
    const across: Vec3 = [Math.cos(heading), -Math.sin(heading) * Math.cos(cameraPitch), Math.sin(heading) * Math.sin(cameraPitch)]
    const sight: Vec3 = [0, Math.sin(cameraPitch), Math.cos(cameraPitch)]
    // Kept on the heading's own side, even for one square across the facing.
    const aim = add(scale(across, Math.max(dot(forward, across), 0.02)), scale(sight, dot(forward, sight)))
    return { yaw: wrapAngle(Math.atan2(aim[0], aim[2]) - facingYaw), pitch: Math.asin(aim[1] / Math.hypot(...aim)) }
}

/**
 * What an animation name asks for. Besides the plain `<action>-<facing>` and the attacks on the move
 * above, there are the settles (settle.ts): `settle.<gait>.<frame>-<facing>` comes to rest from that
 * frame of the gait's stride (`after`, `stride`), `settle.<attack>-<facing>` from a blow struck
 * standing (`after`); and `<attack>.rest-<facing>` is that blow struck from standing (`fromRest`).
 * Any attack can be aimed (`aim`, see aimedShotName); only a clip that aims (a bow's shot) draws it.
 */
export interface ParsedAnimation {
    action: string
    facing: Facing
    gait?: string
    phase?: number
    speed?: number
    after?: string
    stride?: number
    fromRest?: boolean
    /** The screen heading an aimed shot flies along, in radians clockwise from screen right. */
    aim?: number
}

export const parseAnimationName = (animation: string): ParsedAnimation | undefined => {
    const [name, direction] = animation.split("-")
    const aimed = AIM_SEGMENT.exec(name)
    if (!aimed) return parseUnaimed(name, direction)
    const parsed = parseUnaimed(name.slice(0, aimed.index), direction)
    const step = Number(aimed[1])
    if (!parsed || !(parsed.action in LPC_ACTION_DURATION_MS) || parsed.fromRest) return undefined
    if (String(step) !== aimed[1] || step >= SHOT_AIM_STEPS) return undefined
    const aim = step * SHOT_AIM_STEP
    return Math.abs(wrapAngle(aim - facingScreenAngle(parsed.facing))) <= SHOT_AIM_REACH + 1e-9 ? { ...parsed, aim } : undefined
}

const parseUnaimed = (name: string, direction: string | undefined): ParsedAnimation | undefined => {
    const facing = (direction ?? "front") as Facing
    if (!FACINGS.includes(facing)) return undefined
    const [action, gait, phase, pace] = name.split(".")
    if (action === SETTLE) {
        if (MOVING_ATTACK_GAITS.includes(gait)) {
            const stride = Number(phase)
            return pace === undefined && Number.isInteger(stride) && stride >= 0 ? { action, facing, after: gait, stride } : undefined
        }
        return STANCE_ATTACKS.includes(gait) && phase === undefined ? { action, facing, after: gait } : undefined
    }
    // One of the game's actions, or a performance (animations.ts): a clip the body plays when asked.
    if (!Object.prototype.hasOwnProperty.call(CLIP_FOR_ACTION, action) && !isEmberPerformance(action)) return undefined
    if (gait === undefined) return { action, facing }
    if (gait === FROM_REST) return STANCE_ATTACKS.includes(action) && phase === undefined ? { action, facing, fromRest: true } : undefined
    const index = Number(phase)
    if (!(action in LPC_ACTION_DURATION_MS) || !MOVING_ATTACK_GAITS.includes(gait)) return undefined
    if (!Number.isInteger(index) || index < 0 || index >= MOVING_ATTACK_PHASES) return undefined
    if (pace === undefined) return { action, facing, gait, phase: index / MOVING_ATTACK_PHASES }
    const speed = Number(pace)
    if (!Number.isInteger(speed) || speed <= 0 || speed > 1000) return undefined
    return { action, facing, gait, phase: index / MOVING_ATTACK_PHASES, speed }
}

/** Everything about an animation except its pixels, plus a way to render them one frame at a time. */
export interface EmberAnimationPlan extends Omit<EmberAnimation, "frames"> {
    frameCount: number
    renderFrame: (i: number) => Frame
    /** Where the head and fists are on frame `i` (cheap: a pose, no raster). */
    jointsAt: (i: number) => EmberJoints
}

/** What the body carries on top of its humanoid's fixed look: equipment, from the entity. */
export interface EmberEquipment {
    /** An EMBER_HELD name (body.ts) for the right hand, or nothing. */
    held?: string
    /** Its colour variant (`metal`, `brown`), as the inventory item names it. */
    heldColor?: string
    /** Worn leg wear, an EMBER_PANTS name (`pantaloons`): drawn in place of the look's own pants. */
    pants?: string
    /** Its colour, a dye name (`maroon`), as the inventory item names it. */
    pantsColor?: string
    /** Worn body armour, an EMBER_ARMOR name (`armor-plate`), over the look's shirt. */
    armor?: string
    /** Its colour (`metal`, `gold`), as the inventory item names it. */
    armorColor?: string
    /** A worn bauldron, an EMBER_BAULDRON_NAMES type: a pauldron on the right shoulder, strapped on. */
    bauldron?: string
    bauldronColor?: string
    /** A worn shirt, an EMBER_SHIRT_NAMES type (`longsleeve_laced`), in place of the look's own. */
    shirt?: string
    shirtColor?: string
    /** Worn footwear, an EMBER_SHOES name (`boots`), in place of the look's own. */
    shoes?: string
    /** Its colour (a dye, or for armoured shoes a metal), as the inventory item names it. */
    shoesColor?: string
    /**
     * Hair, from the entity's appearance rather than its inventory: any of the game's styles
     * (body.ts HAIR_DESIGNS) in place of the humanoid's own, and its colour.
     */
    hair?: string
    hairColor?: string
    /** The entity's body colour (appearance.bodyColor): a skeleton's bone takes it (`green`). */
    bodyColor?: string
    /** A shield, an EMBER_SHIELD_NAMES type, on the left arm (slung on the back with a bow in hand). */
    shield?: string
    /**
     * Carrying a load in both arms (the game's readied resource or structure, LPC's held object):
     * at rest and on the move the arms hold it in front (clips.ts carrying); the client draws the
     * item between the hands. The hands being full, a weapon is put away and a shield slung meanwhile.
     */
    carry?: boolean
    /** Its colour, as the inventory item names it. */
    shieldColor?: string
}

/**
 * What an Ember body holds and wears, from its entity: the active weapon, the worn shirt, pants,
 * armour, shoes and shield, each when Ember has a drawing of it, and the hair it has chosen. One without is left out rather than guessed at.
 */
export const emberEquipmentOf = (entity: {
    weapon?: { weaponType?: string } | null
    appearance?: { hair?: string; hairColor?: string; bodyColor?: string } | null
    inventory?: { items?: { active?: boolean; itemTypeMeta?: { class?: string; type?: string; color?: string } }[] } | null
}): EmberEquipment => {
    const equipment: EmberEquipment = {}
    const items = entity.inventory?.items ?? []
    const weapon = entity.weapon?.weaponType
    if (weapon && EMBER_HELD_NAMES.includes(weapon)) {
        equipment.held = weapon
        // The colour is the equipped item's: the active one of this type in the inventory.
        const color = items.find(i => i.active && i.itemTypeMeta?.type === weapon)?.itemTypeMeta?.color
        if (color) equipment.heldColor = color
    }
    const pants = items.find(i => i.active && i.itemTypeMeta?.class === "pants")?.itemTypeMeta
    if (pants?.type && EMBER_PANTS_NAMES.includes(pants.type)) {
        equipment.pants = pants.type
        if (pants.color) equipment.pantsColor = pants.color
    }
    const armor = items.find(i => i.active && i.itemTypeMeta?.class === "armor")?.itemTypeMeta
    if (armor?.type && EMBER_ARMOR_NAMES.includes(armor.type)) {
        equipment.armor = armor.type
        if (armor.color) equipment.armorColor = armor.color
    }
    const bauldron = items.find(i => i.active && i.itemTypeMeta?.class === "bauldron")?.itemTypeMeta
    if (bauldron?.type && EMBER_BAULDRON_NAMES.includes(bauldron.type)) {
        equipment.bauldron = bauldron.type
        if (bauldron.color) equipment.bauldronColor = bauldron.color
    }
    if (entity.appearance?.bodyColor) equipment.bodyColor = entity.appearance.bodyColor
    // Hair the entity has chosen; none chosen leaves the humanoid's own.
    const hair = entity.appearance?.hair
    if (hair) {
        equipment.hair = hair
        if (entity.appearance?.hairColor) equipment.hairColor = entity.appearance.hairColor
    }
    const shirt = items.find(i => i.active && i.itemTypeMeta?.class === "shirt")?.itemTypeMeta
    if (shirt?.type && EMBER_SHIRT_NAMES.includes(shirt.type)) {
        equipment.shirt = shirt.type
        if (shirt.color) equipment.shirtColor = shirt.color
    }
    const shoes = items.find(i => i.active && i.itemTypeMeta?.class === "shoes")?.itemTypeMeta
    if (shoes?.type && EMBER_SHOES_NAMES.includes(shoes.type)) {
        equipment.shoes = shoes.type
        if (shoes.color) equipment.shoesColor = shoes.color
    }
    const shield = items.find(i => i.active && i.itemTypeMeta?.class === "shield")?.itemTypeMeta
    if (shield?.type && EMBER_SHIELD_NAMES.includes(shield.type)) {
        equipment.shield = shield.type
        if (shield.color) equipment.shieldColor = shield.color
    }
    return equipment
}

/** One string per distinct look the equipment draws: a cache key for its frames. */
export const emberEquipmentKey = (e: EmberEquipment): string =>
    [
        e.held, e.heldColor, e.pants, e.pantsColor, e.armor, e.armorColor,
        e.shield, e.shieldColor, e.shoes, e.shoesColor, e.hair, e.hairColor, e.shirt, e.shirtColor, e.bodyColor,
        e.bauldron, e.bauldronColor,
        e.carry ? "carry" : "",
        // What the models draw is content: a changed model is a different look.
        emberModelsRevision(),
    ]
        .map(v => v ?? "")
        .join(":")

/** What content's body-colour tables (humanoids.json bodyColors) name for `key`, if anything. */
const named = (table: Record<string, string> | undefined, key: string | undefined): string | undefined =>
    table && key !== undefined && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined

/** The humanoid's look with its equipment on (minus what the humanoid keeps as its own: humanoids.ts keepsOwn). */
const equippedLook = (look: EmberLook, equipment: EmberEquipment, slingShield = false, keepsOwn?: EmberHumanoid["keepsOwn"]): EmberLook => {
    const e = keepsOwn?.length ? ownLook(equipment, keepsOwn) : equipment
    return {
        ...look,
        ...(e.held ? { held: e.held, heldColor: e.heldColor } : {}),
        ...(e.pants ? { pants: wornPants(e.pants, e.pantsColor ?? "brown") } : {}),
        ...(e.armor ? { armor: wornArmor(e.armor, e.armorColor ?? "") } : {}),
        ...(e.bauldron ? { bauldron: wornBauldron(e.bauldron, e.bauldronColor ?? "brown") } : {}),
        ...(e.shoes ? { shoes: wornShoes(e.shoes, e.shoesColor ?? "brown") } : {}),
        ...(e.shirt ? { shirt: wornShirt(e.shirt, e.shirtColor ?? "white") } : {}),
        ...(e.hair ? { hair: { style: e.hair, color: e.hairColor ?? look.hair?.color ?? "brown" } } : {}),
        // Bone takes the body colour, and so does a living man's skin (a zombie's dead flesh is its own).
        ...(e.bodyColor && look.bones ? { boneColor: e.bodyColor } : {}),
        ...(e.bodyColor && !look.bones && named(EMBER_BODY_COLORS.skins, look.skin) && named(EMBER_BODY_COLORS.skins, e.bodyColor) ? { skin: named(EMBER_BODY_COLORS.skins, e.bodyColor) } : {}),
        // A beast's fur is the body colour, in the game's colours for it (an unknown one stays the look's).
        ...(e.bodyColor && look.beast && named(EMBER_BODY_COLORS.furs[look.beast], e.bodyColor) ? { skin: named(EMBER_BODY_COLORS.furs[look.beast], e.bodyColor) } : {}),
        ...(e.shield ? { shield: { type: e.shield, color: e.shieldColor ?? "", slung: slingShield || shieldSlung(e) } } : {}),
    }
}
/** The entity's equipment without the appearance a humanoid keeps as its own (its hair, its skin). */
const ownLook = (e: EmberEquipment, keepsOwn: NonNullable<EmberHumanoid["keepsOwn"]>): EmberEquipment => {
    const { hair, hairColor, bodyColor, ...rest } = e
    return { ...rest, ...(keepsOwn.includes("hair") ? {} : { hair, hairColor }), ...(keepsOwn.includes("skin") ? {} : { bodyColor }) }
}

/** A two-handed weapon (a bow, a polearm) needs the left hand: its shield goes on the back. */
const shieldSlung = (e: EmberEquipment) => !!e.held && TWO_HANDED.includes(e.held)

/** The weapon whose clips a held item plays (its design's handling), its own name by default. */
const clipsOf = (held: string): string => emberDesign("held", held)?.handling?.clips ?? held

/** Whether a body holding `equipment` draws its shots aimed (aimedShotName): one with a bow does. */
export const aimsShots = (equipment: EmberEquipment): boolean => !!equipment.held && !!EMBER_CLIPS[`shoot:${clipsOf(equipment.held)}`]?.aimed

/**
 * A body holding something is drawn on a bigger frame, the body where a 64px frame puts it and the
 * margin all round for the tool's reach -- an axe raised overhead or swung out at arm's length
 * leaves the 64px body frame. The client centres the bigger frame on the same point, as it does
 * LPC's oversize weapon sheets.
 */
export const HELD_FRAME = 96
/**
 * Bigger still for a blade long enough to stand a body's height over the head in a cut, or a
 * polearm carried upright and swung at its full length: the held design's `handling.frame`.
 */
export const heldFrame = (held: string): number => emberDesign("held", held)?.handling?.frame ?? HELD_FRAME
/** A shield in hand needs less room than a weapon's swing, but a falling body flings it past 64px. */
export const SHIELD_FRAME = 80

const withFrame = (style: EmberStyle, size: number): EmberStyle => {
    const grow = (size - style.raster.width) / 2
    return {
        ...style,
        raster: {
            ...style.raster,
            width: size,
            height: size,
            centerX: style.raster.centerX + grow,
            groundY: style.raster.groundY + grow,
        },
    }
}

/**
 * Plan an animation without rendering it, so a caller on a frame budget (the client's baker) can
 * spread the frames over several ticks. Undefined for an unknown humanoid or an action no clip covers.
 * A species body (species.ts) is planned on its own rig, by its own clips.
 */
export const planEmberAnimation = (
    spriteName: string,
    animation: string,
    equipment: EmberEquipment = {},
): EmberAnimationPlan | undefined => {
    const humanoid = EMBER_HUMANOIDS[spriteName]
    if (!humanoid && isEmberSpeciesBody(spriteName)) return planSpeciesAnimation(spriteName, animation, equipment)
    const parsed = parseAnimationName(animation)
    if (!humanoid || !parsed) return undefined
    const baseStyle = EMBER_STYLES[humanoid.style ?? DEFAULT_STYLE]
    // Held things and shields reach past the 64px body frame: a bigger one, centred on the same point.
    // A bigger body's frame grows its held things' frames by as much: its reach is longer too.
    const growth = (humanoid.frame ?? 64) - 64
    const frame = Math.max(
        equipment.held ? heldFrame(equipment.held) + growth : 64,
        equipment.shield ? SHIELD_FRAME + growth : 64,
        humanoid.frame ?? 64,
    )
    const style = frame > 64 ? withFrame(baseStyle, frame) : baseStyle
    // A weapon with an attack of its own (a dagger's slash, a bow's shot) is keyed `<action>:<held>`
    // or `<clip>:<held>`.
    const clips = equipment.held && clipsOf(equipment.held)
    const own = (name: string) => clips && (style.clips?.[`${name}:${clips}`] ?? EMBER_CLIPS[`${name}:${clips}`])
    const clipOf = (name: string) => own(name) || style.clips?.[name] || EMBER_CLIPS[name]
    const proportions = proportionsFor(humanoid.proportions)
    const strideOf = (c: EmberClip) => (c.strideFor ? c.strideFor(proportions) : c.strideCycle)
    // A shield in hand takes the left arm: carried at the hip, raised in an attack.
    const shielded = !!equipment.shield && !shieldSlung(equipment) && !equipment.carry
    const attack = LPC_ACTION_DURATION_MS[parsed.action] !== undefined
    // A zombie shambles over every clip but its fall, reaching out when its hands are empty and
    // it is not striking with them.
    const shambles = humanoid.posture === "shamble"
    // Casts and threats own their arms: neither a shield nor a zombie's reach takes them.
    const casting = parsed.action === "cast" || THREATEN_ACTIONS.includes(parsed.action)
    // A climber's hands are the ladder's: a shield goes on the back.
    const climbing = parsed.action === "climb"
    // A falling body's hands are flung up empty, the weapon put away as on the ladder it is often
    // knocked off -- swung up overhead, a long one swept across the frame and out of it.
    const handsFree = climbing || parsed.action === "falling"
    // A body with a load in its arms holds it at rest and on the move; it strikes, casts or climbs
    // as ever (the load is not drawn then).
    // A stride settling to rest has the load in its arms throughout, as both ends of it do.
    const settlesStride = parsed.action === SETTLE && MOVING_ATTACK_GAITS.includes(parsed.after ?? "")
    const loaded = !!equipment.carry && !parsed.gait && (CARRY_CLIPS.includes(CLIP_FOR_ACTION[parsed.action] ?? "") || isIdleVariation(parsed.action) || settlesStride)
    const bearing = (c: EmberClip, raised: boolean) => {
        if (c.name === "hurt" || handsFree) return c
        const borne = shielded && !casting ? bearingShield(c, raised) : c
        return shambles ? shambling(borne, !raised && !casting && !equipment.held && !shielded) : borne
    }
    // Standing still, a weapon rests as its design holds it and the empty hands hang open (clips.ts
    // atRest): a hand with a weapon (the bow's is the left), a shield or a load in it stays closed.
    // A falling body's hands are empty and fly open, so it sets out from exactly its idle.
    const heldDesign = equipment.held ? emberDesign("held", equipment.held) : undefined
    const holding = new Set<Side>(loaded ? SIDES : [])
    if (heldDesign) holding.add(clipsOf(heldDesign.id) === "bow" ? "L" : "R")
    if (shielded) holding.add("L")
    const still = (c: EmberClip) =>
        (c.name === "idle" || isIdleVariation(c.name)) ? atRest(c, heldDesign?.handling?.rest, SIDES.filter(s => !holding.has(s))) : c.name === "falling" ? atRest(c, undefined, SIDES) : c
    // A polearm is carried upright, wherever the arm swings it (clips.ts poleCarry).
    const carried = (c: EmberClip) =>
        equipment.held && POLEARMS.includes(equipment.held) && (["idle", "walk", "run"].includes(c.name) || isIdleVariation(c.name)) ? poleCarry(still(c)) : still(c)
    // A performance is the whole body's: no shield borne, no shamble, no load carried over it. A
    // hand with something in it stays closed on it where the keys open the hands (rest keys do).
    const performed = isEmberPerformance(parsed.action) ? EMBER_PERFORMANCES[parsed.action] : undefined
    const performance: EmberClip | undefined = performed && holding.size
        ? { ...performed, sample: (t, p, facing) => {
            const pose = performed.sample(t, p, facing)
            const closed = (side: Side): ArmPose => (holding.has(side) ? { ...pose.arms[side], open: undefined } : pose.arms[side])
            return { ...pose, arms: { L: closed("L"), R: closed("R") } }
        } }
        : performed
    // A plain action's clip as this body plays it: borne, carried and loaded as it holds things.
    const clipFor = (action: string) => {
        const c = bearing(carried(own(action) || clipOf(CLIP_FOR_ACTION[action])), LPC_ACTION_DURATION_MS[action] !== undefined)
        return loaded && CARRY_CLIPS.includes(CLIP_FOR_ACTION[action] ?? "") ? carrying(c) : c
    }
    let clip: EmberClip
    if (parsed.action === SETTLE) {
        // Coming to rest (settle.ts): from a frame of a stride, or from a blow struck standing.
        const after = parsed.after!
        const rest = clipFor("idle")
        let settle: EmberClip | undefined
        if (settlesStride) {
            const gait = clipFor(after)
            if (parsed.stride! >= gait.frames) return undefined
            const cyclesPerSecond = SETTLE_SPEED[after] / (strideOf(gait)! * STRIDE_ACROSS_SCREEN[parsed.facing])
            settle = settleFromStride(gait, parsed.stride! / gait.frames, cyclesPerSecond, rest, STRIDE_SETTLES[after], proportions, parsed.facing)
        } else {
            settle = settleFromPose(standingAttack(clipFor(after), rest, false), rest, proportions, parsed.facing)
        }
        if (!settle) return undefined
        clip = settle
    } else {
        // An aimed shot (aimedShotName) is aimed before anything is borne or carried over it.
        const plain = own(parsed.action) || clipOf(CLIP_FOR_ACTION[parsed.action])
        const aimed = parsed.aim !== undefined && plain.aimed ? plain.aimed(shotAim(parsed.facing, parsed.aim, style.raster.pitch)) : plain
        clip = performance ?? bearing(carried(aimed), attack)
        if (loaded && !performance) clip = carrying(clip)
        // A blow struck standing still steps into its stance with the lead foot only, so one step
        // brings the body back out of it; struck from standing, it eases out of the standing pose.
        if (!parsed.gait && !performance && STANCE_ATTACKS.includes(parsed.action)) clip = standingAttack(clip, clipFor("idle"), !!parsed.fromRest)
    }
    let gaitPhaseAfter: number | undefined
    if (parsed.gait) {
        // The legs advance as far through their stride as the attack's duration carries the body.
        const gait = bearing(carried(clipOf(parsed.gait)), false)
        const seconds = LPC_ACTION_DURATION_MS[parsed.action] / 1000
        const speed = parsed.speed ?? MOVING_ATTACK_SPEED[parsed.gait]
        const cycles = (seconds * speed) / (strideOf(gait)! * STRIDE_ACROSS_SCREEN[parsed.facing])
        clip = layerClip(clip, gait, parsed.phase!, cycles)
        gaitPhaseAfter = (parsed.phase! + cycles) % 1
    }
    const stride = parsed.gait ? undefined : strideOf(clip)
    // An attack keeps the duration of the LPC animation it replaces, whatever its frame count.
    const lpcDuration = LPC_ACTION_DURATION_MS[parsed.action]
    return {
        name: animation,
        clip: clip.name,
        facing: parsed.facing,
        frameMs: lpcDuration ? lpcDuration / clip.frames : clip.frameMs,
        loop: clip.loop,
        // What the body travels across the screen per cycle with its planted feet keeping still.
        strideCycle: stride && stride * STRIDE_ACROSS_SCREEN[parsed.facing],
        frameCount: clip.frames,
        gaitPhaseAfter,
        carrying: loaded || undefined,
        renderFrame: i =>
            renderClipFrame(
                // Full hands put the weapon away while they hold the load, or the ladder; so do empty ones in a fall.
                equippedLook(humanoid.look, loaded || handsFree ? { ...equipment, held: undefined } : equipment, handsFree || !!equipment.carry, humanoid.keepsOwn),
                proportions,
                clip,
                clipTime(clip, i, clip.frames),
                parsed.facing,
                style,
            ),
        jointsAt: i => clipJoints(proportions, clip, clipTime(clip, i, clip.frames), parsed.facing, style),
    }
}

export const generateEmberAnimation = (
    spriteName: string,
    animation: string,
    equipment: EmberEquipment = {},
): EmberAnimation | undefined => {
    const plan = planEmberAnimation(spriteName, animation, equipment)
    if (!plan) return undefined
    const { frameCount, renderFrame, ...meta } = plan
    return { ...meta, frames: Array.from({ length: frameCount }, (_, i) => renderFrame(i)) }
}
